import { useEffect, useRef } from "react";

export default function CompanyIntro() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".opacity-0-init").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add("animate-fade-up");
                (el as HTMLElement).style.opacity = "1";
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="py-24 bg-navy relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-inexx-blue/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <p className="opacity-0-init text-inexx-blue text-sm font-semibold tracking-widest uppercase mb-3">About Us</p>
          <h2 className="opacity-0-init text-[24px] md:text-4xl font-bold text-white whitespace-nowrap">iNeXX（アイネックス）について</h2>
          <div className="mx-auto mt-4 w-16 h-0.5 bg-inexx-blue opacity-0-init" />
        </div>

        {/* 2-column layout */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
          {/* Left: Image */}
          <div className="opacity-0-init flex flex-col gap-4 w-full lg:w-1/2">
            {/* Image */}
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{ border: "1px solid rgba(0,119,204,0.3)" }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=modern%20telecommunications%20office%20workspace%20professional%20team%20Japanese%20business%20environment%20dark%20interior%20blue%20accent%20lighting%20server%20room%20high%20tech%20sophisticated%20clean%20minimalist%20corporate&width=800&height=560&seq=inexx-about-01&orientation=landscape"
                alt="iNeXX オフィス"
                className="w-full h-72 md:h-96 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
            </div>
          </div>

          {/* Right: Text */}
          <div className="flex flex-col gap-6 w-full lg:w-1/2">
            <div className="opacity-0-init">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight">
                IT・通信インフラを、
                <br />
                <span className="blue-gradient-text">"人"を通じて支える存在へ。</span>
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                株式会社iNeXXでは、移動体通信を中心に様々なIT業務を提供・支援しております。当社は、高度な技術力と業界経験を結集し、お客様のニーズに合わせリソースを最大限に提供するとともに、お客様からの信頼を得ることで付加価値の高いサービスを提供しています。
              </p>
            </div>

            <div className="opacity-0-init">
              <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                通信全般および携帯電話基地局ネットワークの構築から、資材管理・施工管理・工程管理・保守まで幅広く対応しています。業界を問わず専門知識を持つスタッフが、クライアントのプロジェクトを誠実に、積極的に支援します。
              </p>
            </div>

            {/* Feature list — 3 columns */}
            <div className="opacity-0-init grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
              {[
                { icon: "ri-tools-line", label: "一貫した技術力", desc: "設計から保守まで自社で一貫対応" },
                { icon: "ri-shield-check-line", label: "確かな信頼性", desc: "厳格な品質管理と安全基準の遵守" },
                { icon: "ri-flashlight-line", label: "迅速な対応力", desc: "現場に即したフレキシブルな対応" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-3 bg-navy-light rounded-lg px-4 py-4"
                  style={{ border: "1px solid rgba(0,119,204,0.15)" }}
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-inexx-blue/20 rounded-lg flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-base text-inexx-blue`} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{item.label}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
